import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { entregarVoucher } from '../api/vouchers';
import { mensagemDeErro, MSG_ERRO_REDE } from '../lib/erros';
import { ApiError } from '../lib/http';
import { Drawer } from './Drawer';
import { useToast } from './toast';
import { BlocoAviso, Botao, Campo } from './ui';

/**
 * Registrar entrega de voucher ao portador (UX-PAINEL-INTERNO §7). Reusado pela
 * ficha do cliente e pela busca de voucher. 409 `voucher_ja_entregue` (E10) →
 * frase de concorrência, sem erro técnico.
 */
export function DrawerEntrega({
  voucherId,
  brinde,
  aberto,
  aoFechar,
  aoConcluir,
}: {
  voucherId: string;
  brinde: string;
  aberto: boolean;
  aoFechar: () => void;
  aoConcluir?: () => void;
}): ReactNode {
  const [retiradoPor, setRetiradoPor] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();

  const confirmar = async (): Promise<void> => {
    if (!retiradoPor.trim()) {
      setErro('Informe quem está retirando.');
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      await entregarVoucher(voucherId, retiradoPor.trim());
      toast(`Entrega registrada — ${retiradoPor.trim()} retirou «${brinde}».`);
      await qc.invalidateQueries({ queryKey: ['voucher'] });
      await qc.invalidateQueries({ queryKey: ['cliente'] });
      setRetiradoPor('');
      aoConcluir?.();
      aoFechar();
    } catch (err) {
      if (err instanceof ApiError && err.codigo === 'voucher_ja_entregue') {
        setErro('Outro atendente registrou a entrega deste voucher agora há pouco.');
      } else if (err instanceof ApiError && !err.ehRede) {
        setErro(mensagemDeErro(err.codigo));
      } else {
        setErro(MSG_ERRO_REDE);
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Drawer aberto={aberto} titulo={`Entregar «${brinde}»`} aoFechar={aoFechar}>
      {erro && <BlocoAviso tom="erro">{erro}</BlocoAviso>}
      <Campo
        rotulo="Quem está retirando?"
        placeholder="Nome de quem está levando o brinde"
        autoFocus
        obrigatorio
        value={retiradoPor}
        onChange={(e) => setRetiradoPor(e.target.value)}
      />
      <p className="text-nota text-tinta-2">Isso não pode ser desfeito.</p>
      <div className="flex gap-2">
        <Botao variante="secundario" onClick={aoFechar}>
          Cancelar
        </Botao>
        <Botao carregando={enviando} onClick={() => void confirmar()}>
          Registrar entrega
        </Botao>
      </div>
    </Drawer>
  );
}
